import {noop} from '@benchling/js-utils';
import React from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import GenericNameTemplateField from 'name-templates/GenericNameTemplateField';
import {NameTemplatePart, NameTemplateType} from 'name-templates/NameTemplateConstants';
import SetNameTemplateModal, {SET_NAME_TEMPLATE_MODAL_KEY} from 'name-templates/SetNameTemplateModal';
import createStudyTypeNameTemplateTypeConfiguration from 'name-templates/StudyTypeNameTemplateTypeConfiguration';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {SettingsPopover} from 'ui-library/SettingsField';

export interface ProcedureRunNameTemplateFieldProps {
  schemaId: string;
  schemaPrefix: string;
  studyType: StudyType;
  nameTemplateParts: Array<NameTemplatePart>;
  isWritable: boolean; // whether we allow changing the name template
  setNameTemplateParts: (parts: Array<NameTemplatePart>) => Promise<boolean>;
}

export default class ProcedureRunNameTemplateField extends React.Component<ProcedureRunNameTemplateFieldProps> {
  nameTemplateTypeConfiguration = createStudyTypeNameTemplateTypeConfiguration(this.props.studyType);

  openSetNameTemplateModal = (): void => {
    LegacyModalContainer.open(
      SET_NAME_TEMPLATE_MODAL_KEY,
      <SetNameTemplateModal
        bioentityType=""
        schemaFields={[]}
        sequenceType={null}
        schemaId={this.props.schemaId}
        initialNameTemplateParts={this.props.nameTemplateParts}
        nameTemplateType={NameTemplateType.PROCEDURE_RUN}
        nameTemplateTypeConfiguration={this.nameTemplateTypeConfiguration}
        itemType={null}
        onRequestSubmit={async (submitArgs) => {
          const didSubmit = await this.props.setNameTemplateParts(submitArgs.nameTemplateParts);
          if (didSubmit) {
            this.closeSetNameTemplateModal();
          }
        }}
        onRequestClose={this.closeSetNameTemplateModal}
      />
    );
  };

  closeSetNameTemplateModal = (): void => {
    LegacyModalContainer.close(SET_NAME_TEMPLATE_MODAL_KEY);
  };

  override render(): JSX.Element {
    const procedureType = getProcedureTypeForStudyType(this.props.studyType);
    const procedureLabel = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType);
    const procedureLabelLowerCase = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    });
    const helpIconContent = (
      <SettingsPopover
        title={`${procedureLabel} run name template`}
        renderContent={() => (
          <div className="tagSchemaSettings-popover">
            <p>
              You can automatically name {procedureLabelLowerCase} runs based on a naming template. If the
              name template is changed, it will not affect the names of existing {procedureLabelLowerCase}{' '}
              runs.
            </p>
          </div>
        )}
      />
    );

    return (
      <GenericNameTemplateField
        label={`${procedureLabel} run name template`}
        sequenceType={null}
        bioentityType=""
        {...this.props}
        schemaFields={[]}
        helpIconContent={helpIconContent}
        setNameTemplateParts={this.openSetNameTemplateModal}
        // Run name templates are not deletable since they are always required to be present for all procedure
        // based studies (Clinical, Preclinical, Process Development and Assay Development).
        isDeletable={false}
        removeNameTemplateParts={noop}
        testAttrComponent="ProcedureRunNameTemplateField"
        nameTemplateTypeConfiguration={this.nameTemplateTypeConfiguration}
      />
    );
  }
}
